import _ from 'lodash';
import { observable, action, computed, runInAction, toJS, flow } from 'mobx';
import Validator from 'validatorjs';
import en from 'validatorjs/src/lang/en';
import { trackPublish } from '../core/analytics';
import moment from 'moment';
Validator.setMessages('en', en);
import { getListingData } from './../components_new/addListing/getListingQL';

const groups = {
  description: ['title', 'description', 'categories'],
  photos: ['photos', 'coverPhotoName'],
  address: ['addresses'],
  price: ['basePrice'],
  rules: ['rules'],
  review: ['termsAccepted', 'liabilityAccepted'],
};

const validationRules = {
  title: 'required|min:3',
  description: 'required|min:10',
  categories: 'required|array|min:1|max:2',
  basePrice: 'required|integer|min:1|max:9999',
  addresses: 'required|array|min:1',
  blockedDates: 'array',
  rules: 'array',
  photos: 'required|array|min:1',
  coverPhotoName: 'required|min:5',
  termsAccepted: 'required',
  liabilityAccepted: 'required',
};

const publishValidationRules = {
  title: 'required|min:3',
  description: 'required|min:10',
  categories: 'required|array|min:1|max:2',
  basePrice: 'required|integer|min:1|max:9999',
  addresses: 'required|array|min:1',
  blockedDates: 'array',
  rules: 'array',
  photos: 'required|array|min:1',
  coverPhotoName: 'required|min:5',
};

const validStates = ['none', 'pending', 'success', 'failure'];

export default class AddListingModel {
  @observable _title = '';
  @observable _description = '';
  @observable _categories = [];
  @observable _basePrice = 0;
  @observable _addresses = [];
  @observable _buildingName = '';
  @observable _blockedDates = [];
  @observable _reservedDates = [];
  @observable _rules = [];
  @observable _photos = [];
  @observable _coverPhotoName = '';
  @observable _liabilityAccepted = false;
  @observable _termsAccepted = false;

  @observable changed = {};

  @observable _listId = null;

  @observable saveState = null;
  @observable mostRecentResults = null;
  @observable currentStep = 0;
  @observable largestStep = 0;
  @observable editing = false;
  @observable nextStep = '';
  @observable published = false;
  @observable groupsVisited = {
    intro: false,
    description: false,
    photos: false,
    address: false,
    price: false,
    rules: false,
    availability: false,
    review: false,
    publish: false,
    success: false,
  };

  @observable allSteps = [
    'intro',
    'description',
    'address',
    'price',
    'rules',
    'availability',
    'photos',
    'review',
    'success',
  ];

  constructor(getListingQ) {
    this.getListingQ = getListingQ;
  }

  @computed get timelineSteps() {
    return this.allSteps.filter((x) => x !== 'intro' && x !== 'success');
  }

  @computed get reviewSteps() {
    return this.timelineSteps.filter((x) => x !== 'review');
  }

  @computed
  get currentStepName() {
    return this.allSteps[this.currentStep];
  }

  @computed
  get nextStepName() {
    if (this.currentStep === this.allSteps.length - 1) {
      return 'Done';
    }
    return this.allSteps[this.currentStep + 1];
  }

  @computed
  get previousStepName() {
    if (this.currentStep === 0) {
      return null;
    }
    return this.allSteps[this.currentStep - 1];
  }

  uppercaseFirst(text) {
    if (!text || text.length < 2) {
      return text;
    }
    return text[0].toUpperCase() + text.substring(1);
  }

  @computed
  get nextStepText() {
    const nextText =
      (this.currentStepName === 'publish' && 'Publish') ||
      (this.hasChanged && 'Save & Next: ' + this.uppercaseFirst(this.nextStepName)) ||
      'Next: ' + this.uppercaseFirst(this.nextStepName);
    return nextText;
  }

  @computed
  get canNext() {
    return !this.groupsVisited[this.currentStepName] || this.currentGroupValid;
  }

  @computed
  get validation() {
    const values = {
      title: this.title,
      description: this.description,
      categories: this.categories,
      basePrice: this.basePrice,
      addresses: this.addresses,
      buildingName: this.buildingName,
      blockedDates: this.blockedDates,
      resevedDates: this.resevedDates,
      rules: this.rules,
      photos: this.photos,
      coverPhotoName: this.coverPhotoName,
      liabilityAccepted: this.liabilityAccepted,
      termsAccepted: this.termsAccepted,
    };
    let validation = new Validator(values, validationRules);
    return {
      isValid: validation.passes(),
      errors: validation.errors,
    };
  }

  @computed
  get publishValidation() {
    const values = {
      title: this.title,
      description: this.description,
      categories: this.categories,
      basePrice: this.basePrice,
      addresses: this.addresses,
      buildingName: this.buildingName,
      blockedDates: this.blockedDates,
      resevedDates: this.resevedDates,
      rules: this.rules,
      photos: this.photos,
      coverPhotoName: this.coverPhotoName,
      liabilityAccepted: this.liabilityAccepted,
      termsAccepted: this.termsAccepted,
    };
    let validation = new Validator(values, publishValidationRules);
    return {
      isValid: validation.passes(),
      errors: validation.errors,
    };
  }

  groupErrors(groupName) {
    return _.flatten(
      _.map(groups[groupName], (v) => {
        return this.validation.errors.get(v);
      })
    );
  }

  @computed
  get currentGroupErrors() {
    return _.flatten(
      _.map(groups[this.currentStepName], (v) => {
        return this.validation.errors.get(v);
      })
    );
  }

  @computed
  get currentGroupValid() {
    return this.currentGroupErrors.length === 0;
  }

  fieldErrors(fieldName) {
    return this.validation.errors.get(fieldName);
  }

  nextErrorForField(fieldName) {
    const errors = this.validation.errors.get(fieldName);
    return errors.length > 0 ? errors[0] : '';
  }

  @computed
  get status() {
    const result = {
      editing: this.editing,
      published: this.published,
      steps: [],
    };
    this.timelineSteps.forEach((step) => {
      result.steps.push({
        name: step,
        label: this.uppercaseFirst(step),
        hasErrors: this.groupErrors(step).length > 0,
        errors: this.groupErrors(step),
        visited: this.groupsVisited[step],
        current: this.currentStepName === step,
        canVisit: (this.groupsVisited[step] && step !== this.currentStepName) || this.editing,
      });
    });

    return result;
  }

  @action
  visit(groupName) {
    this.groupsVisited[groupName] = true;
  }

  @action
  reInit() {
    this._title = null;
    this._description = null;
    this._categories = [];
    this._basePrice = null;
    this._addresses = [];
    this._blockedDates = [];
    this._rules = [];
    this._photos = [];
    this._coverPhotoName = null;
    this._liabilityAccepted = false;
    this._termsAccepted = false;
    this.changed = {};
    this._listId = null;
    this.saveState = 'none';
    this.mostRecentResults = null;
    this.currentStep = 0;
    this.largestStep = 0;
    this.editing = false;
    this.nextStep = '';
    this.published = false;
    this.groupsVisited = {
      description: false,
      photos: false,
      address: false,
      price: false,
      rules: false,
      review: false,
      publish: false,
    };
  }

  @action
  addRule(ruleText) {
    if (!ruleText || !ruleText.trim()) {
      return;
    }
    const nRules = this.rules.slice();
    nRules.push({
      description: ruleText,
    });
    this.rules = nRules;
  }

  @action
  deleteRule(ruleText, id) {
    if (id) {
      const rules = this.rules.slice();
      const nRules = rules.filter((x) => x.id !== id);
      this.rules = nRules;
    } else if (ruleText) {
      const rules = this.rules.slice();
      const nRules = rules.filter((x) => x.description !== ruleText);
      this.rules = nRules;
    }
  }

  @action
  setSaveState(newVal) {
    if (validStates.indexOf(newVal) < 0) {
      console.log('save states must be : ', validStates, newVal);
      return;
    }
    this.saveState = newVal;
  }

  @action moveTo(stepNumOrName) {
    if (typeof stepNumOrName === 'number') {
      this.currentStep = stepNumOrName;
    } else {
      this.currentStep = this.allSteps.indexOf(stepNumOrName);
    }
    this.nextStep = '';
  }
  @action moveToUrl(urlPageName) {
    this.currentStep = this.allSteps.indexOf(urlPageName);
    this.nextStep = '';
    const stepNum = this.allSteps.indexOf(urlPageName);
    for (let i = stepNum - 1; i >= 0; i--) {
      this.groupsVisited[this.allSteps[i]] = true;
    }
  }

  @action
  async save(fetch) {
    const vals = {};
    if (Object.keys(this.changed).length === 0) {
      return;
    }
    _.forEach(this.changed, (v, k) => {
      vals[k] = toJS(v); //slice is a mobx slice that gives back just the value
    });
    if (this.listId) {
      vals.id = this.listId;
    }

    if (vals.blockedDates) {
      vals.blockedDates = vals.blockedDates.map((x) => moment(x).format('YYYY-MM-DD'));
    }
    if (vals.addresses) {
      vals.addresses = vals.addresses.map((x) => x.id);
    }
    if (vals.rules) {
      vals.rules = vals.rules.map((x) => x.description);
    }
    if (vals.categories) {
      vals.categories = vals.categories.map((x) => +x);
    }

    this.setSaveState('pending');

    let failed = false;
    const result = await fetch(vals).catch((err) => {
      console.log('AddListingModel.js - error saving listing', err);
      runInAction(() => {
        this.setSaveState('failure');
      });
      failed = true;
    });

    if (failed) return;

    runInAction(() => {
      this.setSaveState('success');
      this.mostRecentResults = result.data.createListing;
      if (!this.listId) {
        this._listId = result.data.createListing.id;
      }

      // console.log(
      //   'AddListingModel.js -save Success',
      //   this.listId,
      //   this.mostRecentResults,
      // );
      _.forEach(this.changed, (v, k) => {
        if (k === 'categories') {
          const newCats = [undefined, undefined];
          const oldCat1 = this._categories[0];
          const newCat1 = toJS(v)[0];
          const oldCat2 = this._categories[1];
          const newCat2 = toJS(v)[1];
          if (newCat1) {
            newCats[0] = newCat1;
          } else if (oldCat1) {
            newCats[0] = oldCat1;
          }
          if (newCat2) {
            newCats[1] = newCat2;
          } else if (oldCat2) {
            newCats[1] = oldCat2;
          }
          this._categories = newCats;
        } else {
          this['_' + k] = toJS(v);
        }
      });
      this.changed = {};
    });
  }

  @computed get hasChanged() {
    return Object.keys(this.changed).length > 0;
  }

  addToArray(value, prop) {
    if (!this.changed[prop]) {
      this.changed[prop] = [];
    }
    this.changed[prop].push(value);
  }

  @computed get title() {
    return this.changed.title !== undefined ? this.changed.title : this._title;
  }
  set title(value) {
    this.changed.title = value ? value.trimStart() : value;
  }
  @computed get description() {
    return this.changed.description !== undefined ? this.changed.description : this._description;
  }
  set description(value) {
    this.changed.description = value ? value.trimStart() : value;
  }
  @computed get categories() {
    return this.changed.categories !== undefined ? this.changed.categories : this._categories;
  }
  @action addCategory(value) {
    this.addToArray(value, 'categories');
  }
  @action addSingleCategory(value) {
    const cats = this.changed.categories;
    if (cats) {
      if (cats.length === 0) {
        this.changed.categories.push(value);
      } else {
        this.changed.categories[0] = value;
      }
    } else {
      this.changed.categories = [value];
    }
  }
  @action addSecondCategory(value) {
    const cats = this.changed.categories;
    if (cats) {
      if (cats.length === 0) {
        this.changed.categories = [undefined, value];
      } else if (cats.length === 1) {
        this.changed.categories.push(value);
      } else {
        this.changed.categories[1] = value;
      }
    } else {
      this.changed.categories = [undefined, value];
    }
  }
  @computed get firstCategory() {
    return (this.categories.length > 0 && this.categories[0]) || null;
  }
  @computed get secondCategory() {
    return (this.categories.length > 1 && this.categories[1]) || null;
  }
  set categories(value) {
    this.changed.categories = value;
  }
  @computed get basePrice() {
    return this.changed.basePrice !== undefined ? this.changed.basePrice : this._basePrice;
  }
  set basePrice(value) {
    this.changed.basePrice = value;
  }

  @computed get addresses() {
    return this.changed.addresses !== undefined ? this.changed.addresses : this._addresses;
  }
  set addresses(value) {
    this.changed.addresses = value;
  }
  @computed get blockedDates() {
    return this.changed.blockedDates !== undefined ? this.changed.blockedDates : this._blockedDates;
  }
  set blockedDates(value) {
    this.changed.blockedDates = value;
  }

  @action addBlockedDates(value) {
    this.addToArray(value, 'blockedDates');
  }
  @computed get reservedDates() {
    return this.changed.reservedDates !== undefined
      ? this.changed.reservedDates
      : this._reservedDates;
  }
  set reservedDates(value) {
    this.changed.reservedDates = value;
  }
  @action addReservedDates(value) {
    this.addToArray(value, 'reservedDates');
  }

  @computed get rules() {
    return this.changed.rules !== undefined ? this.changed.rules : this._rules;
  }
  set rules(value) {
    this.changed.rules = value;
  }

  @computed get photos() {
    return this.changed.photos !== undefined ? this.changed.photos : this._photos;
  }
  set photos(value) {
    this.changed.photos = value;
  }

  @action addPhoto(value) {
    this.addToArray(value, 'photos');
  }
  @computed get coverPhotoName() {
    return this.changed.coverPhotoName !== undefined
      ? this.changed.coverPhotoName
      : this._coverPhotoName;
  }
  set coverPhotoName(value) {
    this.changed.coverPhotoName = value;
  }

  @computed get termsAccepted() {
    return this.changed.termsAccepted !== undefined
      ? this.changed.termsAccepted
      : this._termsAccepted;
  }
  set termsAccepted(value) {
    this.changed.termsAccepted = value;
  }

  @computed get liabilityAccepted() {
    return this.changed.liabilityAccepted !== undefined
      ? this.changed.liabilityAccepted
      : this._liabilityAccepted;
  }
  set liabilityAccepted(value) {
    this.changed.liabilityAccepted = value;
  }

  @computed get listId() {
    return this._listId;
  }
  set listId(value) {
    this._listId = value;
    this.getListing();
  }

  @action
  getListing = flow(function*() {
    if (!this.listId) {
      return null;
    }
    console.log('AddListingModel.js - GETTING LISTING WITH', this.listId);
    const result = yield this.getListingQ(this.listId);
    // console.log('AddListingModel.js - GOT LISTING WITH', this.listId, result);
    if (result) {
      this._addresses = result.listingAddresses;
      this._coverPhotoName = result.coverPhotoName;
      this._description = result.description;
      this._rules = result.listingRules;
      this._photos = result.listPhotos;
      this._basePrice = result.basePrice;
      this._title = result.title;
      this._categories = [result.category, result.category2];
      this.published = result.isPublished;
      this._blockedDates = result.blockedDates
        .filter((d) => !d.reservationId)
        .map((d) => new Date(d.blockedDates));
      this._reservedDates = result.blockedDates
        .filter((d) => d.reservationId)
        .map((d) => new Date(d.blockedDates));
    }
  });

  @action
  async deletePhoto(gqlFetcher, name) {
    try {
      this.setSaveState('pending');
      const isCover = name === this.coverPhotoName;
      // debugger;
      const data = await gqlFetcher(this.listId, name);
      // console.log('Deleted Photo', data);

      if (data) {
        // Remove file physically
        runInAction(() => {
          this.setSaveState('success');
          if (isCover) {
            this.coverPhotoName = null;
          }
          const newPhotos = this.photos.slice().filter((x) => {
            const comparison = x.name !== name;
            // console.log('Comparing', x.name, name, comparison, x);
            return comparison;
          });
          // console.log('Photos after delete', newPhotos, 'CURRENT', this.photos);
          this._photos = newPhotos;
        });
      }
    } catch (error) {
      // console.log('addListing/graphQL.js - Failed to delete photos', error);
      runInAction(() => {
        this.setSaveState('failure');
        return false;
      });
    }

    return true;
  }

  @action
  async uploadPhotos(uploadFetcher, saveFetcher) {
    const photos = this.changed.photos;

    this.setSaveState('pending');
    // console.log('AddListingModel.js - uploading photos');
    this._photosTmp = [];

    const promises = photos.map(async (photo, index) => {
      const data = new FormData();
      data.append('file', {
        uri: photo.uri,
        type: 'image/jpeg',
        name: 'image' + index,
      });
      try {
        const res = await uploadFetcher(data);
        runInAction(() => {
          // console.log('Successfully uploaded');
          this.setSaveState('success');
          this._photosTmp.push(res.data.files[0]);
        });
        return res;
      } catch (err) {
        console.log('upload err', err);
        runInAction(() => {
          this.setSaveState('failure');
        });
      }
    });
    // console.log('UPLOAD PROMISES', promises);
    Promise.all(promises).then(() => {
      runInAction(() => {
        // console.log('All photos uploaded', this._photosTmp);
        this.savePhotos(saveFetcher);
        delete this.changed.photos;
      });
    });
  }

  @action
  async savePhotos(saveFetcher, photoFiles) {
    // console.log('AddListingModel.js - saving photos 1');
    try {
      // console.log('SAVING PHOTOS 1');
      const photosBase = photoFiles || this._photosTmp;
      // console.log('AddListingModel.js - saving photos 2');
      let listId = this.listId;
      // console.log('AddListingModel.js - saving photos 3');
      if (!listId || !photosBase || !photosBase.length) {
        // console.log(
        //   'AddListingModel.js - saving photos 3X',
        //   listId,
        //   photosBase
        // );
        return;
      }

      this.setSaveState('pending');

      // console.log('AddListingModel.js - saving photos 4');

      // console.log('SAVING PHOTOS 2');
      //Send photo names to server to save
      const photoPromises = photosBase.map(async (photo, index) => {
        let name = photo.fileName;
        let type = photo.fileType;

        return saveFetcher({ listId, name, type });
      });

      //Once all photo saves are done add to store
      Promise.all(photoPromises).then((res) => {
        // console.log('SAVING PHOTOS 3');
        const photos = res.map((x) => ({
          name: x.CreateListingPhotos.name,
          id: x.CreateListingPhotos.id,
          order: x.CreateListingPhotos.order,
        }));
        runInAction(() => {
          // console.log('Successfully Saved Photos', photos);
          // console.log('SAVING PHOTOS 4');
          this.setSaveState('success');
          const p2 = this._photos.slice();
          photos.forEach((p) => {
            p2.push(p);
            if (p.order === 0) {
              this.coverPhotoName = p.name;
            }
          });
          this._photos = p2;
          // console.log('SAVING PHOTOS 5', this._photos);
        });
      });

      return;
    } catch (err) {
      console.log('Photo save err', err);
      runInAction(() => {
        this.setSaveState('failure');
        this._photos.push(res);
      });
    }
  }

  @action updatePhotoOrder() {
    console.log('updating photo Order', this.coverPhotoName);
    const photos = this.photos.slice();
    const coverName = this.coverPhotoName;
    const newCover = photos.find((e) => e.name === coverName);
    const newOrder = photos.filter((photo) => photo.name !== coverName);
    if (newCover) {
      newOrder.unshift(newCover);
    }
    newOrder.forEach((itm, idx) => (itm.order = idx));
    this.photos = newOrder;
  }

  @action
  updateCoverPhoto = flow(function*(fetcherFn) {
    try {
      this.setSaveState('pending');
      const newOrder = toJS(this.photos);
      const updated = yield fetcherFn(this.listId, newOrder);
      this._photos = this.photos;
      const coverPhoto = this._photos.filter((x) => x.order === 0)[0];
      // console.log('updating cover photo', coverPhoto.name);
      this.coverPhotoName = coverPhoto.name;
      delete this.changed.photos;
      this.setSaveState('success');
    } catch (err) {
      console.log('Failed to update photo order', err);
    }
  });

  updatePublish = flow(function*(fetcher) {
    try {
      yield fetcher(this.listId, this.published ? 'unPublish' : 'publish');
      trackPublish(this.published ? 'unPublish' : 'publish');
      this.published = !this.published;
    } catch (err) {
      console.log('Error swapping publish state', err, 'published: ', this.published);
    }
  });

  buildDetails() {
    return {
      listPhotos: toJS(this.photos),
      title: this.title,
      basePrice: this.basePrice,
      description: this.description,
    };
  }
}



// WEBPACK FOOTER //
// ./src/mobxStores/AddListingModel.js